import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { fetchWidgetData, fetchInventoryWidgetData, fetchInventorySample } from '@api/dataSource';
import { loadHostTimezones, loadHostTimezonesByName } from '@api/filters';
import { formatCellValue } from '@common/utils/dateUtils';
import WidgetContainer from '../WidgetContainer';
import * as styles from './TableWidget.module.css';

/**
 * TableChartWidget - Renders data in a paginated table format
 *
 * Features:
 * - Supports regular SQL queries and inventory data sources
 * - Row Grouping: When enabled (via `enableRowGrouping` prop or automatically for inventory),
 *   consecutive identical values in the first column are merged into a single cell with rowSpan.
 *   The merged cell content stays visible in the middle of the viewport while scrolling.
 * - Pagination with configurable page sizes
 * - Dashboard filter integration
 *
 * Row Grouping Implementation:
 * 1. Data is sorted by the first column to group identical values together
 * 2. `calculateRowSpans()` computes which rows should be merged (returns rowSpan values)
 * 3. Cells with rowSpan > 1 render with the HTML rowSpan attribute
 * 4. Cells with rowSpan = 0 are skipped (they're covered by the spanning cell above)
 * 5. A scroll handler positions the merged cell content to stay visible in the viewport
 */
function TableChartWidget({
  widget,
  dashboardArgs = {},
  dashboardFilters = {},
  isEditing = false,
  enableRowGrouping = false,
}) {
  const { t } = useTranslation();
  const { config } = widget;
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const tableWrapperRef = useRef(null);
  const groupedCellRefs = useRef({});
  const rafRef = useRef(null);

  // Check if this is an inventory data source
  // Memoize to prevent unnecessary re-renders of dependent callbacks
  const widgetDataSources = useMemo(() => config?.dataSources || [], [config?.dataSources]);
  const isInventoryDataSource = widgetDataSources.some((ds) => ds.type === 'inventory');

  // Extract parameter placeholders from the query
  // Query is now inside custom_query dataSource's config
  const queryParams = useMemo(() => {
    const customQueryDs = (config?.dataSources || []).find((ds) => ds.type === 'custom_query');
    const queryText = customQueryDs?.config?.query;
    if (!queryText) return [];
    const paramRegex = /\{\{(\w+)\}\}/g;
    const matches = [];
    let match = paramRegex.exec(queryText);
    while (match !== null) {
      matches.push(match[1]);
      match = paramRegex.exec(queryText);
    }
    return [...new Set(matches)];
  }, [config]);

  // Get only relevant dashboard args for this widget
  const relevantArgs = useMemo(() => {
    if (queryParams.length === 0) return null;
    const filtered = {};
    queryParams.forEach((param) => {
      if (dashboardArgs[param] !== undefined) {
        filtered[param] = dashboardArgs[param];
      }
    });
    return Object.keys(filtered).length > 0 ? JSON.stringify(filtered) : null;
  }, [queryParams, dashboardArgs]);

  /**
   * Precompute group boundaries from span info for efficient pagination.
   * Returns array of { start, end, size } for each group.
   */
  const computeGroupBoundaries = useCallback((spanInfo, totalRows) => {
    const groups = [];
    let idx = 0;

    while (idx < totalRows) {
      const spanValue = spanInfo[idx] || 1;
      if (spanValue > 0) {
        groups.push({
          start: idx,
          end: idx + spanValue,
          size: spanValue,
        });
        idx += spanValue;
      } else {
        // Should not happen if data is well-formed, but handle gracefully
        idx += 1;
      }
    }

    return groups;
  }, []);

  /**
   * Compute page ranges for grouped data.
   * Each page contains whole groups that fit within pageSize rows.
   * Returns array of { startGroupIdx, endGroupIdx, startRow, endRow } for each page.
   */
  const computeGroupedPageRanges = useCallback((groups, size) => {
    if (groups.length === 0) return [];

    const pages = [];
    let currentPageStartGroup = 0;
    let currentPageRowCount = 0;

    for (let i = 0; i < groups.length; i += 1) {
      const group = groups[i];

      // If adding this group would exceed page size, finalize the current page
      // Exception: if it's the first group of a page, include it even if it exceeds size
      if (currentPageRowCount > 0 && currentPageRowCount + group.size > size) {
        pages.push({
          startGroupIdx: currentPageStartGroup,
          endGroupIdx: i - 1,
          startRow: groups[currentPageStartGroup].start,
          endRow: groups[i - 1].end,
        });
        currentPageStartGroup = i;
        currentPageRowCount = 0;
      }

      currentPageRowCount += group.size;
    }

    // Add the last page
    if (currentPageStartGroup < groups.length) {
      pages.push({
        startGroupIdx: currentPageStartGroup,
        endGroupIdx: groups.length - 1,
        startRow: groups[currentPageStartGroup].start,
        endRow: groups[groups.length - 1].end,
      });
    }

    return pages;
  }, []);

  /**
   * Get paginated data for inventory with row grouping.
   * Uses pre-computed page ranges to ensure groups are never split across pages.
   * Returns { pageData, pageSpans, actualPageStart, actualPageEnd, totalPages }
   */
  const getPaginatedDataWithGrouping = useCallback(
    (allRows, spanInfo, page, size) => {
      if (allRows.length === 0) {
        return { pageData: [], pageSpans: {}, actualPageStart: 0, actualPageEnd: 0, totalPages: 0 };
      }

      // Precompute group boundaries
      const groups = computeGroupBoundaries(spanInfo, allRows.length);
      if (groups.length === 0) {
        return { pageData: [], pageSpans: {}, actualPageStart: 0, actualPageEnd: 0, totalPages: 0 };
      }

      // Compute page ranges based on groups
      const pageRanges = computeGroupedPageRanges(groups, size);
      if (pageRanges.length === 0) {
        return { pageData: [], pageSpans: {}, actualPageStart: 0, actualPageEnd: 0, totalPages: 0 };
      }

      // Clamp page index to valid range
      const safePage = Math.min(page, pageRanges.length - 1);
      const pageRange = pageRanges[safePage];

      const startIdx = pageRange.startRow;
      const endIdx = pageRange.endRow;

      // Slice the data
      const pageData = allRows.slice(startIdx, endIdx);

      // Recalculate spans for the page (indices reset to 0)
      const pageSpans = {};
      for (let i = startIdx; i < endIdx; i += 1) {
        pageSpans[i - startIdx] = spanInfo[i];
      }

      return {
        pageData,
        pageSpans,
        actualPageStart: startIdx,
        actualPageEnd: endIdx,
        totalPages: pageRanges.length,
      };
    },
    [computeGroupBoundaries, computeGroupedPageRanges],
  );

  /**
   * Calculate row spans for the first column to group consecutive identical values.
   * Returns an object mapping row indices to their rowSpan values.
   * Rows that should be hidden (part of a span) have rowSpan = 0.
   */
  const calculateRowSpans = useCallback((rows, columnKey = '0') => {
    const spans = {};
    let i = 0;

    while (i < rows.length) {
      const currentValue = rows[i][columnKey];
      let spanCount = 1;

      // Count consecutive rows with the same value
      while (i + spanCount < rows.length && rows[i + spanCount][columnKey] === currentValue) {
        spanCount += 1;
      }

      // First row in the group gets the full span
      spans[i] = spanCount;

      // Subsequent rows in the group are hidden (rowSpan = 0)
      for (let j = 1; j < spanCount; j += 1) {
        spans[i + j] = 0;
      }

      i += spanCount;
    }

    return spans;
  }, []);

  // Helper function to translate inventory column names
  const translateInventoryColumn = useCallback(
    (columnName) => {
      // Map backend column names to i18n keys for inventory widgets
      const columnMap = {
        Host: t('dashboard.widgets.inventory.columns.host'),
        Category: t('dashboard.widgets.inventory.columns.category'),
        Item: t('dashboard.widgets.inventory.columns.item'),
        Value: t('dashboard.widgets.inventory.columns.value'),
        'Host Count': t('dashboard.widgets.inventory.columns.hostCount', 'Host Count'),
        'Hosts with item': t('dashboard.widgets.inventory.columns.hostsWithItem', 'Hosts with item'),
        'Values summary': t('dashboard.widgets.inventory.columns.valuesSummary', 'Values summary'),
        'Unique Values': t('dashboard.widgets.inventory.columns.uniqueValues', 'Unique Values'),
      };
      return columnMap[columnName] || columnName;
    },
    [t],
  );

  /**
   * Check if a value is considered empty for column filtering.
   * Returns true for null, undefined, empty string, or whitespace-only string.
   */
  const isEmptyValue = useCallback((value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' || trimmed === '-';
    }
    return false;
  }, []);

  /**
   * Check if a column has all empty values.
   * Used to hide columns (like "Value") when they contain no data.
   */
  const isColumnEmpty = useCallback(
    (rows, columnIndex) => rows.every((row) => isEmptyValue(row[columnIndex])),
    [isEmptyValue],
  );

  /**
   * Filter out empty columns from inventory data.
   * Returns { filteredColumns, filteredRows }
   */
  const filterEmptyColumns = useCallback(
    (apiColumns, apiRows) => {
      // If no rows, return columns as-is (can't determine which are empty)
      if (!apiRows || apiRows.length === 0) {
        return { filteredColumns: apiColumns, filteredRows: apiRows };
      }

      // Find indices of non-empty columns
      const nonEmptyIndices = apiColumns.map((_, index) => index).filter((index) => !isColumnEmpty(apiRows, index));

      // If all columns have data, return as-is
      if (nonEmptyIndices.length === apiColumns.length) {
        return { filteredColumns: apiColumns, filteredRows: apiRows };
      }

      // If no columns have data, keep all columns (edge case - show the structure at least)
      if (nonEmptyIndices.length === 0) {
        return { filteredColumns: apiColumns, filteredRows: apiRows };
      }

      // Filter columns and rows
      const filteredColumns = nonEmptyIndices.map((idx) => apiColumns[idx]);
      const filteredRows = apiRows.map((row) => nonEmptyIndices.map((idx) => row[idx]));

      return { filteredColumns, filteredRows };
    },
    [isColumnEmpty],
  );

  // Get timezone settings from dashboard filters
  const timezoneType = dashboardFilters?.timeRange?.timezone; // 'local' or 'host'

  // Host timezone cache: { hostKey: gmtOffset } where hostKey is either host_id or host_name
  const [hostTimezoneCache, setHostTimezoneCache] = useState({});

  // Reset host timezone cache when timezone type changes
  useEffect(() => {
    setHostTimezoneCache({});
  }, [timezoneType]);

  /**
   * Format a cell value with timezone awareness
   * Uses the shared formatCellValue utility from dateUtils
   * @param {unknown} value - Cell value to format
   * @param {string} columnName - Column name for detecting timestamp columns
   * @param {string|number|null} hostKey - Host identifier (id or name) for timezone lookup
   */
  const formatValue = useCallback((value, columnName, hostKey = null) => {
    const options = {
      timezoneType,
      gmtOffset: timezoneType === 'host' ? hostTimezoneCache[String(hostKey)] : null,
    };
    return formatCellValue(value, columnName, options);
  }, [timezoneType, hostTimezoneCache]);

  /**
   * Process split_by_category layout data.
   * Flattens multiple category tables into a single table with Category as first column.
   * @param {Object} tables - Backend response tables { categoryName: { columns, rows, total } }
   * @returns {{ columnDefs: Array, rowData: Array, spans: Object, total: number }}
   */
  const processSplitByCategoryData = useCallback(
    (tables) => {
      const flatRows = [];
      const rawColumns = ['Category', 'Host', 'Item', 'Value'];

      // Backend returns tables as { categoryName: { columns, rows, total } }
      Object.entries(tables).forEach(([category, tableData]) => {
        if (tableData && Array.isArray(tableData.rows)) {
          tableData.rows.forEach((subRow) => {
            // subRow is [Host, Item, Value], we prepend Category as first column
            // Defensive check: ensure subRow has at least 3 elements
            if (Array.isArray(subRow) && subRow.length >= 3) {
              flatRows.push([category, subRow[0], subRow[1], subRow[2]]);
            }
          });
        }
      });

      // Filter out empty columns (e.g., Value column when all values are empty)
      const { filteredColumns, filteredRows } = filterEmptyColumns(rawColumns, flatRows);

      // Sort by Category (first column) to group them together
      // Use dynamic column indices in case filtering shifts positions
      const catIdx = filteredColumns.indexOf('Category');
      const hostIdx = filteredColumns.indexOf('Host');
      filteredRows.sort((a, b) => {
        const catA = String(a[catIdx] || '').toLowerCase();
        const catB = String(b[catIdx] || '').toLowerCase();
        if (catA !== catB) return catA.localeCompare(catB);
        // Secondary sort by Host (if Host column exists)
        if (hostIdx >= 0) {
          const hostA = String(a[hostIdx] || '').toLowerCase();
          const hostB = String(b[hostIdx] || '').toLowerCase();
          return hostA.localeCompare(hostB);
        }
        return 0;
      });

      // Translate column names for i18n
      const columnDefs = filteredColumns.map((col, index) => ({
        accessorKey: String(index),
        header: translateInventoryColumn(col),
        cell: ({ getValue }) => formatValue(getValue(), col),
      }));

      const rowData = filteredRows.map((row) =>
        row.reduce((acc, val, idx) => {
          acc[String(idx)] = val;
          return acc;
        }, {}),
      );

      // Calculate row spans for the first column (Category)
      const spans = calculateRowSpans(rowData, '0');

      return { columnDefs, rowData, spans, total: rowData.length };
    },
    [filterEmptyColumns, translateInventoryColumn, formatValue, calculateRowSpans],
  );

  /**
   * Process standard inventory data with row grouping enabled.
   * Filters empty columns, sorts by first column, and calculates row spans.
   * @param {Array} apiColumns - Column names from API
   * @param {Array} apiRows - Row data from API
   * @returns {{ columnDefs: Array, rowData: Array, spans: Object, total: number }}
   */
  const processGroupedInventoryData = useCallback(
    (apiColumns, apiRows) => {
      // Filter out columns that are entirely empty
      const { filteredColumns, filteredRows } = filterEmptyColumns(apiColumns, apiRows);
      const workingRows = [...filteredRows];

      // Sort by first column to enable grouping
      if (filteredColumns.length > 0) {
        workingRows.sort((a, b) => {
          const valA = String(a[0] || '').toLowerCase();
          const valB = String(b[0] || '').toLowerCase();
          return valA.localeCompare(valB);
        });
      }

      // Translate inventory column names for i18n support
      const columnDefs = filteredColumns.map((col, index) => ({
        accessorKey: String(index),
        header: translateInventoryColumn(col),
        cell: ({ getValue }) => formatValue(getValue(), col),
      }));

      const rowData = workingRows.map((row) =>
        row.reduce((acc, val, idx) => {
          acc[String(idx)] = val;
          return acc;
        }, {}),
      );

      // Calculate row spans for the first column
      const spans = calculateRowSpans(rowData, '0');

      return { columnDefs, rowData, spans, total: rowData.length };
    },
    [filterEmptyColumns, translateInventoryColumn, formatValue, calculateRowSpans],
  );

  /**
   * Process standard inventory data without row grouping.
   * Filters empty columns and creates column definitions.
   * @param {Array} apiColumns - Column names from API
   * @param {Array} apiRows - Row data from API
   * @param {number} serverTotal - Total count from server
   * @returns {{ columnDefs: Array, rowData: Array, total: number }}
   */
  const processStandardInventoryData = useCallback(
    (apiColumns, apiRows, serverTotal) => {
      // Filter out columns that are entirely empty
      const { filteredColumns, filteredRows } = filterEmptyColumns(apiColumns, apiRows);

      // Translate inventory column names for i18n support
      const columnDefs = filteredColumns.map((col, index) => ({
        accessorKey: String(index),
        header: translateInventoryColumn(col),
        cell: ({ getValue }) => formatValue(getValue(), col),
      }));

      const rowData = filteredRows.map((row) =>
        row.reduce((acc, val, idx) => {
          acc[String(idx)] = val;
          return acc;
        }, {}),
      );

      return { columnDefs, rowData, total: serverTotal ?? rowData.length };
    },
    [filterEmptyColumns, translateInventoryColumn, formatValue],
  );

  /**
   * Determine valid table layout based on row type.
   * @param {string} layout - Configured layout
   * @param {string} rowType - 'hosts' or 'items'
   * @returns {string} Valid layout for the row type
   */
  const getValidTableLayout = useCallback((layout, rowType) => {
    if (rowType === 'items') {
      // For 'items' row type: only 'aggregated' or 'expanded' are valid
      return layout && ['aggregated', 'expanded'].includes(layout) ? layout : 'aggregated';
    }
    // For 'hosts' row type: 'long_form', 'split_by_category', or 'pivot' are valid
    return layout && ['long_form', 'split_by_category', 'pivot'].includes(layout) ? layout : 'long_form';
  }, []);

  // Resolve effective page size (preview mode overrides user page size)
  const effectivePageSize = widget.previewLimit || pageSize;

  // Sync preview limit into page size state when it changes
  useEffect(() => {
    if (widget.previewLimit && pageSize !== widget.previewLimit) {
      setPageSize(widget.previewLimit);
      setPageIndex(0);
    }
  }, [widget.previewLimit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset page index when filters, query args, or config change
  useEffect(() => {
    setPageIndex(0);
  }, [dashboardFilters, relevantArgs, config]);

  // --- Regular table data (server-side pagination) ---
  const hasTableSource = widgetDataSources.some((ds) => ds.type === 'table');
  const hasCustomQuery = widgetDataSources.some((ds) => ds.type === 'custom_query' && ds.config?.query);
  const regularQueryEnabled =
    !isInventoryDataSource && (hasTableSource || hasCustomQuery) && (!isEditing || !!widget.previewLimit);

  const regularQuery = useQuery({
    queryKey: [
      'table-widget',
      widget.id,
      'regular',
      config,
      dashboardFilters,
      dashboardArgs,
      pageIndex,
      effectivePageSize,
      relevantArgs,
    ],
    queryFn: async () => {
      const tableSources = config?.dataSources?.filter((ds) => ds.type === 'table') || [];
      const customQuerySource = config?.dataSources?.find((ds) => ds.type === 'custom_query');

      const response = await fetchWidgetData(
        'table',
        config,
        dashboardFilters,
        isEditing,
        null,
        dashboardArgs,
        pageIndex + 1,
        effectivePageSize,
      );

      if (response.status !== 'success') {
        throw new Error(response.message || response.error || 'Failed to load data');
      }

      const { columns: apiColumns, rows: apiRows, total } = response.data;
      const tableSource = tableSources[0];
      const selectedColumns = tableSource?.config?.selectedColumns || customQuerySource?.config?.selectedColumns;

      if (apiRows.length === 0) {
        const headerNames =
          selectedColumns?.length > 0
            ? selectedColumns.map((col) => (col.includes('.') ? col.split('.').pop() : col))
            : apiColumns;

        return { columnNames: headerNames, data: [], totalCount: total || 0 };
      }

      let finalColumns = apiColumns;
      let finalRows = apiRows;

      // Find host_id or host_name column in original API response (for timezone lookup)
      const hostIdColNames = ['host_id', 'host id', 'hostid'];
      const hostNameColNames = ['host_name', 'host name', 'hostname', 'host'];
      const hostIdColIndex = apiColumns.findIndex((col) =>
        hostIdColNames.includes(col.toLowerCase()));
      const hostNameColIndex = apiColumns.findIndex((col) =>
        hostNameColNames.includes(col.toLowerCase()));

      // Track which column we're using for timezone lookup
      let tzLookupColIndex = -1;

      // If using host timezone, fetch host timezones for all hosts in the data
      if (timezoneType === 'host') {
        if (hostIdColIndex !== -1) {
          // Prefer host_id lookup
          tzLookupColIndex = hostIdColIndex;
          const hostIds = [...new Set(
            apiRows.map((row) => row[hostIdColIndex]).filter((id) => id != null),
          )];
          if (hostIds.length > 0) {
            const tzMap = await loadHostTimezones(hostIds);
            setHostTimezoneCache((prev) => ({ ...prev, ...tzMap }));
          }
        } else if (hostNameColIndex !== -1) {
          // Fallback to host_name lookup
          tzLookupColIndex = hostNameColIndex;
          const hostNames = [...new Set(
            apiRows.map((row) => row[hostNameColIndex]).filter((name) => name != null && name !== ''),
          )];
          if (hostNames.length > 0) {
            const tzMap = await loadHostTimezonesByName(hostNames);
            setHostTimezoneCache((prev) => ({ ...prev, ...tzMap }));
          }
        }
      }

      if (selectedColumns && selectedColumns.length > 0) {
        const selectedIndices = selectedColumns
          .map((colName) => apiColumns.indexOf(colName))
          .filter((index) => index !== -1);

        if (selectedIndices.length > 0) {
          finalColumns = selectedIndices.map((idx) => apiColumns[idx]);
          finalRows = apiRows.map((row) => selectedIndices.map((index) => row[index]));
        }
      }

      // Return raw column names - cell renderers will be built outside query
      // to avoid stale closures with formatValue
      const columnNames = finalColumns;

      // Build row data, preserving host_id or host_name for timezone lookup in cell renderer
      const rowData = finalRows.map((row, rowIdx) => {
        const rowObj = row.reduce((acc, val, idx) => {
          acc[String(idx)] = val;
          return acc;
        }, {});
        // Store host identifier for timezone lookup (prefer id, fallback to name)
        if (tzLookupColIndex !== -1 && apiRows[rowIdx]) {
          rowObj.hostTzKey = apiRows[rowIdx][tzLookupColIndex];
        }
        return rowObj;
      });

      return { columnNames, data: rowData, totalCount: total || rowData.length };
    },
    enabled: regularQueryEnabled,
    gcTime: 0,
    retry: false,
  });

  // --- Inventory data (client-side pagination) ---
  // Skip fetching if widget.data is already populated (e.g., from WidgetWizard preview)
  const hasPreloadedData = !!(widget.data?.columns && widget.data?.rows);
  const inventoryQueryEnabled =
    isInventoryDataSource && !!config && !hasPreloadedData && (!isEditing || !!widget.previewLimit);

  const inventoryQuery = useQuery({
    queryKey: ['table-widget', widget.id, 'inventory', config, dashboardFilters, enableRowGrouping, isEditing],
    queryFn: async () => {
      const inventorySource = widgetDataSources.find((ds) => ds.type === 'inventory');
      const dsConfig = inventorySource?.config || {};

      const { rowType: configRowType, tableLayout: configTableLayout } = dsConfig;
      const rowType = configRowType || 'hosts';
      const tableLayout = getValidTableLayout(configTableLayout, rowType);

      const inventoryConfig = {
        inventoryRowType: rowType,
        inventoryTableLayout: tableLayout,
        inventoryCategoryHandling: dsConfig.categoryHandling || 'column',
        inventorySelectedCategories: dsConfig.selectedCategories || [],
        inventoryScanBasis: dsConfig.scanBasis || 'latest',
        inventoryPivotColumnLimit: dsConfig.pivotColumnLimit ?? 20,
      };

      // In edit mode, use /inventory/sample to get only 3 hosts (same as WidgetWizard preview)
      // In view mode, use /inventory/widget-data to get all hosts with dashboard filters
      const response = isEditing
        ? await fetchInventorySample(inventoryConfig, 3)
        : await fetchInventoryWidgetData(inventoryConfig, dashboardFilters);

      if (response.status !== 'success' || !response.data) {
        throw new Error(response.message || t('common.error'));
      }

      const { columns: apiColumns, rows: apiRows, total, tables } = response.data;

      if (tableLayout === 'split_by_category' && tables) {
        const result = processSplitByCategoryData(tables);
        return {
          columns: result.columnDefs,
          allData: result.rowData,
          allSpans: result.spans,
          totalCount: result.total,
          grouped: true,
        };
      }

      if (enableRowGrouping) {
        const result = processGroupedInventoryData(apiColumns, apiRows);
        return {
          columns: result.columnDefs,
          allData: result.rowData,
          allSpans: result.spans,
          totalCount: result.total,
          grouped: true,
        };
      }

      const result = processStandardInventoryData(apiColumns, apiRows, total);
      return {
        columns: result.columnDefs,
        allData: result.rowData,
        allSpans: {},
        totalCount: result.total,
        grouped: false,
      };
    },
    enabled: inventoryQueryEnabled,
    gcTime: 0,
    retry: false,
  });

  // Transform preloaded widget.data into query format (when data comes from WidgetWizard preview)
  const preloadedInventoryData = useMemo(() => {
    if (!hasPreloadedData || !isInventoryDataSource) return null;

    const { columns: preloadedColumns, rows: preloadedRows, total: preloadedTotal } = widget.data;
    if (!preloadedColumns || !preloadedRows) return null;

    // Transform to same format as inventoryQuery returns
    if (enableRowGrouping) {
      const result = processGroupedInventoryData(preloadedColumns, preloadedRows);
      return {
        columns: result.columnDefs,
        allData: result.rowData,
        allSpans: result.spans,
        totalCount: result.total,
        grouped: true,
      };
    }

    const result = processStandardInventoryData(preloadedColumns, preloadedRows, preloadedTotal);
    return {
      columns: result.columnDefs,
      allData: result.rowData,
      allSpans: {},
      totalCount: result.total,
      grouped: false,
    };
  }, [
    hasPreloadedData,
    isInventoryDataSource,
    widget.data,
    enableRowGrouping,
    processGroupedInventoryData,
    processStandardInventoryData,
  ]);

  // Use preloaded data if available, otherwise use query data
  const effectiveInventoryData = preloadedInventoryData || inventoryQuery.data;

  // Derive client-side pagination for inventory data
  const inventoryPage = useMemo(() => {
    const invData = effectiveInventoryData;
    if (!invData) return null;

    if (invData.grouped && invData.allData.length > 0 && Object.keys(invData.allSpans).length > 0) {
      return getPaginatedDataWithGrouping(invData.allData, invData.allSpans, pageIndex, effectivePageSize);
    }

    // Non-grouped inventory: simple slice with standard pagination
    const start = pageIndex * effectivePageSize;
    const nonGroupedTotalPages = Math.ceil(invData.totalCount / effectivePageSize);
    return {
      pageData: invData.allData.slice(start, start + effectivePageSize),
      pageSpans: {},
      actualPageStart: start,
      totalPages: nonGroupedTotalPages,
    };
  }, [effectiveInventoryData, pageIndex, effectivePageSize, getPaginatedDataWithGrouping]);

  // --- Unified derived state ---
  // Build column definitions with cell renderers here (not inside query)
  // to ensure formatValue always has the latest hostTimezoneCache
  const columns = useMemo(() => {
    if (isInventoryDataSource) return effectiveInventoryData?.columns || [];

    const columnNames = regularQuery.data?.columnNames || [];
    return columnNames.map((col, index) => ({
      accessorKey: String(index),
      header: col,
      cell: ({ getValue, row }) => formatValue(getValue(), col, row.original.hostTzKey),
    }));
  }, [isInventoryDataSource, effectiveInventoryData, regularQuery.data, formatValue]);

  const data = useMemo(() => {
    if (isInventoryDataSource) return inventoryPage?.pageData || [];
    return regularQuery.data?.data || [];
  }, [isInventoryDataSource, inventoryPage, regularQuery.data]);

  const totalCount = isInventoryDataSource
    ? effectiveInventoryData?.totalCount || 0
    : regularQuery.data?.totalCount || 0;

  // For grouped inventory data, use the pre-computed totalPages from getPaginatedDataWithGrouping
  // This ensures page boundaries align with group boundaries
  const groupedTotalPages = inventoryPage?.totalPages;

  const rowSpanInfo = useMemo(() => inventoryPage?.pageSpans || {}, [inventoryPage]);
  const actualPageStart = inventoryPage?.actualPageStart || 0;

  const loading = regularQuery.isLoading || inventoryQuery.isLoading;
  const error = useMemo(() => {
    const err = regularQuery.error || inventoryQuery.error;
    if (!err) return null;
    if (err.statusCode === 408) return t('dataSource.errors.timeoutDefault');
    return err.message || t('common.error');
  }, [regularQuery.error, inventoryQuery.error, t]);

  // Handle scroll to keep grouped cell content sticky in the middle of visible area
  useEffect(() => {
    const tableWrapper = tableWrapperRef.current;
    if (!tableWrapper || !enableRowGrouping || Object.keys(rowSpanInfo).length === 0) return undefined;

    const TABLE_HEADER_HEIGHT = 44;

    const updatePositions = () => {
      const headerHeight = TABLE_HEADER_HEIGHT;
      const wrapperRect = tableWrapper.getBoundingClientRect();
      const visibleHeight = wrapperRect.height - headerHeight;
      const middleOfVisible = visibleHeight / 2;

      Object.entries(groupedCellRefs.current).forEach(([, refs]) => {
        if (!refs || !refs.cell || !refs.content) return;

        const { cell, content } = refs;
        const cellRect = cell.getBoundingClientRect();
        const wrapperTop = wrapperRect.top + headerHeight;
        const contentHeight = content.offsetHeight;

        const cellTopRelative = cellRect.top - wrapperTop;
        const cellBottomRelative = cellRect.bottom - wrapperTop;
        const cellHeight = cellRect.height;

        const targetPosition = middleOfVisible - contentHeight / 2;

        let translateY;

        if (cellTopRelative > targetPosition) {
          translateY = 0;
        } else if (cellBottomRelative < targetPosition + contentHeight) {
          translateY = cellHeight - contentHeight;
        } else {
          translateY = targetPosition - cellTopRelative;
        }

        translateY = Math.max(0, Math.min(translateY, cellHeight - contentHeight));
        content.style.transform = `translateY(${translateY}px)`;
      });
    };

    const onScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updatePositions);
    };

    tableWrapper.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updatePositions);
    updatePositions();

    return () => {
      tableWrapper.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updatePositions);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [rowSpanInfo, data, enableRowGrouping]);

  // Create table instance
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pageSize),
  });

  // Show edit mode message only if no preview data is available
  if (isEditing && !widget.previewLimit && !data.length) {
    return <WidgetContainer noData noDataMessage={t('dashboard.widgets.configureToSeeData')} />;
  }

  // Show loading/error states
  if (loading || error) {
    return <WidgetContainer loading={loading} error={error} />;
  }

  // No data and no columns to show structure — fall back to generic no-data state
  if (data.length === 0 && columns.length === 0) {
    return <WidgetContainer noData noDataMessage={t('dashboard.widgets.pagination.noDataMessage')} />;
  }

  // Use grouped totalPages when available for accurate group-aware pagination
  const totalPages = groupedTotalPages || Math.ceil(totalCount / pageSize);

  return (
    <div className={styles.container}>
      <div className={styles.tableWrapper} ref={tableWrapperRef}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={styles.th}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={styles.tbody}>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.noDataCell}>
                  {t('dashboard.widgets.pagination.noDataMessage')}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row, rowIndex) => (
                <tr key={row.id} className={styles.tr}>
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    // For the first column, apply row span logic if available
                    if (cellIndex === 0 && Object.keys(rowSpanInfo).length > 0) {
                      const spanValue = rowSpanInfo[rowIndex];

                      // Skip rendering if this cell is part of a span (rowSpan = 0)
                      if (spanValue === 0) {
                        return null;
                      }

                      // Render with rowSpan if grouping multiple rows
                      if (spanValue > 1) {
                        return (
                          <td
                            key={cell.id}
                            className={`${styles.td} ${styles.groupedCell}`}
                            rowSpan={spanValue}
                            ref={(el) => {
                              if (!groupedCellRefs.current[rowIndex]) {
                                groupedCellRefs.current[rowIndex] = {};
                              }
                              groupedCellRefs.current[rowIndex].cell = el;
                            }}
                          >
                            <div
                              className={styles.groupedCellContent}
                              title={String(cell.getValue() ?? '')}
                              ref={(el) => {
                                if (!groupedCellRefs.current[rowIndex]) {
                                  groupedCellRefs.current[rowIndex] = {};
                                }
                                groupedCellRefs.current[rowIndex].content = el;
                              }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </div>
                          </td>
                        );
                      }

                      // Single row, no grouping needed
                      return (
                        <td key={cell.id} className={styles.td}>
                          <div className={styles.cellContent} title={String(cell.getValue() ?? '')}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </div>
                        </td>
                      );
                    }

                    // Regular cell rendering
                    return (
                      <td key={cell.id} className={styles.td}>
                        <div className={styles.cellContent} title={String(cell.getValue() ?? '')}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data.length > 0 && (
        <div className={styles.pagination}>
          <div className={styles.paginationLeft}>
            <span className={styles.pageInfo}>
              {t('dashboard.widgets.pagination.showing', {
                // Use actualPageStart for grouping-aware pagination, fallback to calculated value for standard pagination
                from: (isInventoryDataSource ? actualPageStart : pageIndex * pageSize) + 1,
                to: (isInventoryDataSource ? actualPageStart : pageIndex * pageSize) + data.length,
                total: totalCount,
              })}
            </span>
          </div>

          <div className={styles.paginationCenter}>
            <button
              type="button"
              onClick={() => setPageIndex(0)}
              disabled={pageIndex === 0}
              className={styles.paginationButton}
            >
              {'<<'}
            </button>
            <button
              type="button"
              onClick={() => setPageIndex((p) => p - 1)}
              disabled={pageIndex === 0}
              className={styles.paginationButton}
            >
              {'<'}
            </button>
            <span className={styles.currentPage}>
              {t('dashboard.widgets.pagination.page', {
                current: pageIndex + 1,
                total: totalPages,
              })}
            </span>
            <button
              type="button"
              onClick={() => setPageIndex((p) => p + 1)}
              disabled={pageIndex >= totalPages - 1}
              className={styles.paginationButton}
            >
              {'>'}
            </button>
            <button
              type="button"
              onClick={() => setPageIndex(totalPages - 1)}
              disabled={pageIndex >= totalPages - 1}
              className={styles.paginationButton}
            >
              {'>>'}
            </button>
          </div>

          <div className={styles.paginationRight}>
            <label htmlFor="pageSize" className={styles.pageSizeLabel}>
              {t('dashboard.widgets.pagination.rowsPerPage')}:
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageIndex(0);
                }}
                className={styles.pageSizeSelect}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

TableChartWidget.propTypes = {
  widget: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    title: PropTypes.string,
    previewLimit: PropTypes.number,
    config: PropTypes.shape({
      dataSource: PropTypes.string,
      query: PropTypes.string,
      table: PropTypes.string,
      columns: PropTypes.arrayOf(PropTypes.string),
      selectedColumns: PropTypes.arrayOf(PropTypes.string),
      // Inventory config
      inventoryRowType: PropTypes.string,
      inventoryTableLayout: PropTypes.string,
      inventoryCategoryHandling: PropTypes.string,
      inventorySelectedCategories: PropTypes.arrayOf(PropTypes.string),
      inventoryScanBasis: PropTypes.string,
      inventoryPivotColumnLimit: PropTypes.number,
    }),
  }).isRequired,
  dashboardArgs: PropTypes.shape({}),
  dashboardFilters: PropTypes.shape({}),
  isEditing: PropTypes.bool,
  /**
   * Enable row grouping for the first column.
   * When enabled, consecutive identical values in the first column are merged
   * into a single cell with rowSpan, and the content stays visible while scrolling.
   */
  enableRowGrouping: PropTypes.bool,
};

TableChartWidget.defaultProps = {
  dashboardArgs: {},
  dashboardFilters: {},
  isEditing: false,
  enableRowGrouping: false,
};

export default TableChartWidget;
